/**
 * Toast Component
 *
 * Accessible notification component for temporary messages.
 * Built with Radix UI for proper ARIA live regions and keyboard support.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X } from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface ToastProps extends ComponentPropsWithoutRef<typeof ToastPrimitive.Root> {
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  title?: string;
  description?: string;
  action?: React.ReactNode;
}

export type ToastViewportProps = ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>;

export type ToastActionProps = ComponentPropsWithoutRef<typeof ToastPrimitive.Action>;

/**
 * Toast Provider - Wrap your app with this
 */
const ToastProvider = ToastPrimitive.Provider;

/**
 * Toast Viewport - Place this in your app layout (e.g., App.tsx)
 */
const ToastViewport = forwardRef<
  ElementRef<typeof ToastPrimitive.Viewport>,
  ToastViewportProps
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    className={cn(
      'fixed top-0 right-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4',
      'sm:right-0 sm:top-0 sm:flex-col md:max-w-md',
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitive.Viewport.displayName;

/**
 * Toast Component
 *
 * @example
 * ```tsx
 * <Toast variant="success" title="Success!" description="Your changes have been saved.">
 *   <ToastClose />
 * </Toast>
 * ```
 */
const Toast = forwardRef<ElementRef<typeof ToastPrimitive.Root>, ToastProps>(
  ({ className, variant = 'default', title, description, action, children, ...props }, ref) => {
    const variantStyles = {
      default: 'bg-white border-neutral-200',
      success: 'bg-green-50 border-green-200 text-green-900',
      warning: 'bg-amber-50 border-amber-200 text-amber-900',
      error: 'bg-red-50 border-red-200 text-red-900',
      info: 'bg-primary-50 border-primary-200 text-primary-900',
    };

    return (
      <ToastPrimitive.Root
        ref={ref}
        className={cn(
          // Base styles
          'group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden',
          'rounded-lg border p-4 shadow-lg transition-all',

          // Animation
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[swipe=end]:animate-out data-[state=closed]:fade-out-80',
          'data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full',
          'data-[state=open]:sm:slide-in-from-top-full',

          // WCAG 2.1 AA contrast
          variantStyles[variant],

          className
        )}
        {...props}
      >
        <div className="flex flex-1 flex-col gap-1">
          {title && (
            <ToastPrimitive.Title className="text-sm font-semibold">
              {title}
            </ToastPrimitive.Title>
          )}
          {description && (
            <ToastPrimitive.Description className="text-sm opacity-90">
              {description}
            </ToastPrimitive.Description>
          )}
          {action}
        </div>
        {children}
      </ToastPrimitive.Root>
    );
  }
);
Toast.displayName = ToastPrimitive.Root.displayName;

/**
 * Toast Action - Button for user action
 */
const ToastAction = forwardRef<ElementRef<typeof ToastPrimitive.Action>, ToastActionProps>(
  ({ className, ...props }, ref) => (
    <ToastPrimitive.Action
      ref={ref}
      className={cn(
        // BFSG compliant 44px height
        'inline-flex h-11 shrink-0 items-center justify-center rounded-md border px-4',
        'text-sm font-medium transition-colors',

        // Focus ring - WCAG 2.1 AA
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',

        // Hover state
        'hover:bg-neutral-100',

        // Border
        'border-neutral-200',

        className
      )}
      {...props}
    />
  )
);
ToastAction.displayName = ToastPrimitive.Action.displayName;

/**
 * Toast Close Button
 */
const ToastClose = forwardRef<
  ElementRef<typeof ToastPrimitive.Close>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => {
  const { t } = useTranslation();
  return (
  <ToastPrimitive.Close
    ref={ref}
    className={cn(
      // BFSG compliant 44x44px touch target
      'absolute right-2 top-2 rounded-md p-1 opacity-70 transition-opacity',
      'hover:opacity-100',

      // Focus ring - WCAG 2.1 AA
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',

      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-5 w-5" />
    <span className="sr-only">{t('a11y.close')}</span>
  </ToastPrimitive.Close>
  );
});
ToastClose.displayName = ToastPrimitive.Close.displayName;

export { ToastProvider, ToastViewport, Toast, ToastAction, ToastClose };
